import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { authApi } from "@/features/auth/api";

type State = "verifying" | "success" | "error";

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState<State>(token ? "verifying" : "error");
  const ran = useRef(false);

  useEffect(() => {
    if (!token || ran.current) return;
    ran.current = true; // guard React 18 StrictMode double-invoke
    authApi
      .verifyEmail(token)
      .then(() => setState("success"))
      .catch(() => setState("error"));
  }, [token]);

  return (
    <div className="w-full max-w-sm space-y-5 text-center">
      <h1 className="text-2xl font-semibold tracking-tight">Email Verification</h1>

      {state === "verifying" && (
        <div className="flex items-center justify-center gap-2 rounded-md border bg-muted/30 px-3 py-4 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Verifying your email…
        </div>
      )}

      {state === "success" && (
        <>
          <div className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-3 text-left text-sm text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300">
            <CheckCircle2 className="mt-0.5 size-4 shrink-0" />
            <p>Your email has been verified. You now have full access.</p>
          </div>
          <Button asChild className="w-full">
            <Link to="/dashboard">Go to Dashboard</Link>
          </Button>
        </>
      )}

      {state === "error" && (
        <>
          <div className="flex items-start gap-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-3 text-left text-sm text-destructive">
            <XCircle className="mt-0.5 size-4 shrink-0" />
            <p>
              This verification link is invalid or has expired. Sign in and use
              the "Resend verification" option to get a new one.
            </p>
          </div>
          <Button asChild variant="outline" className="w-full">
            <Link to="/login">Back to Sign In</Link>
          </Button>
        </>
      )}
    </div>
  );
}
